import React, { useState } from 'react';
import { MapPin, Globe, Phone, Share2, X, ChevronDown, ChevronUp } from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
  EmailIcon
} from 'react-share';
import type { Practitioner, Location } from '../types';
import Map from './Map';

interface PractitionerCardProps {
  practitioner: Practitioner;
  location: Location;
}

export default function PractitionerCard({ practitioner, location }: PractitionerCardProps) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  
  const shareUrl = `${window.location.origin}/practitioners/${practitioner.id}`;
  const shareTitle = `Check out ${practitioner.name} - Reiki Practitioner`;
  const shareDescription = `${practitioner.name} offers Reiki healing services in ${location.city}, ${location.state}. Book your session today!`;

  // Function to determine if a street view URL is likely a profile photo
  const isStreetViewProfilePhoto = (url: string): boolean => {
    if (url.includes('googleusercontent.com') || url.includes('AF1QipM')) {
      return true;
    }
    if (url.includes('streetviewpixels-pa.googleapis.com')) {
      return false;
    }
    return false;
  };

  // Use street view URL if available, fallback to profile image or default image
  const displayImage = practitioner.street_view_url || practitioner.image_url || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b';
  
  // Determine if the image is a profile photo
  const isProfilePhoto = !practitioner.street_view_url ? 
    !!practitioner.image_url : 
    isStreetViewProfilePhoto(practitioner.street_view_url);

  // Generate star rating display
  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span 
          key={i} 
          className={`text-lg ${i <= rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div 
      className={`bg-white dark:bg-dark-secondary rounded-lg shadow-md overflow-hidden transition-all duration-300 ${
        isExpanded ? 'ring-2 ring-purple-500 shadow-lg' : 'hover:shadow-lg'
      }`}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className={`relative transition-all duration-300 ${isExpanded ? 'h-96' : 'h-48'}`}>
        <img
          src={displayImage}
          alt={`${practitioner.name} ${isProfilePhoto ? 'profile' : 'location'}`}
          className={`w-full h-full object-cover transition-all duration-300 ${isProfilePhoto ? 'object-[50%_35%]' : 'object-center'}`}
        />
        <div className="absolute top-4 right-4 flex space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowShareModal(true);
            }}
            className="p-2 bg-white/90 dark:bg-dark-secondary/90 rounded-full shadow-md hover:bg-white dark:hover:bg-dark-secondary transition-colors duration-200"
            aria-label="Share"
          >
            <Share2 className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
        <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 transition-opacity duration-300 ${isExpanded ? 'opacity-100' : 'opacity-0'}`}>
          <h3 className="text-xl font-semibold text-white">
            {practitioner.name}
          </h3>
          {location && (
            <p className="text-white/90 text-sm mt-1">
              {location.city}, {location.state}
            </p>
          )}
        </div>
      </div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {practitioner.name}
          </h3>
        </div>

        {/* Google Rating */}
        {practitioner.google_rating && (
          <div className="flex items-center gap-2 mb-4">
            <div className="flex">{renderStars(practitioner.google_rating)}</div>
            <span className="text-gray-600 dark:text-gray-300">
              {practitioner.google_rating.toFixed(1)} ({practitioner.google_review_count || 0} reviews)
            </span>
          </div>
        )}

        {/* Basic Info */}
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-300">
            {isExpanded ? (practitioner.description || '') : 
              practitioner.description ? 
                `${practitioner.description.slice(0, 150)}${practitioner.description.length > 150 ? '...' : ''}` : 
                ''}
          </p>

          {/* Contact Info */}
          <div className="space-y-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMap(!showMap);
              }}
              className="flex items-center w-full text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200"
            >
              <MapPin className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="text-left">{location.address}, {location.city}, {location.state} {location.zip}</span>
              {showMap ? (
                <ChevronUp className="h-5 w-5 ml-2" />
              ) : (
                <ChevronDown className="h-5 w-5 ml-2" />
              )}
            </button>
            
            {showMap && (
              <div className="mt-4" onClick={e => e.stopPropagation()}>
                <Map
                  locations={[location]}
                  selectedLocationId={location.id}
                />
              </div>
            )}

            {practitioner.phone && (
              <div className="flex items-center text-gray-500 dark:text-gray-400">
                <Phone className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{practitioner.phone}</span>
              </div>
            )}
            {practitioner.website && (
              <div className="flex items-center text-gray-500 dark:text-gray-400">
                <Globe className="h-5 w-5 mr-2 flex-shrink-0" />
                <a
                  href={practitioner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 dark:text-purple-400 hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>

          {/* Additional Info - Only Visible When Expanded */}
          <div className={`space-y-4 overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
            {/* Google Reviews Section */}
            {practitioner.google_reviews && practitioner.google_reviews.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Google Reviews
                  </h4>
                  {practitioner.google_reviews.length > 3 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAllReviews(!showAllReviews);
                      }}
                      className="text-purple-600 dark:text-purple-400 text-sm hover:underline"
                    >
                      {showAllReviews ? 'Show Less' : 'Show All'}
                    </button>
                  )}
                </div>
                <div className="space-y-4">
                  {(showAllReviews ? practitioner.google_reviews : practitioner.google_reviews.slice(0, 3)).map((review) => (
                    <div key={review.id} className="bg-gray-50 dark:bg-dark-accent rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        {review.profile_photo_url && (
                          <img
                            src={review.profile_photo_url}
                            alt={review.author_name}
                            className="w-8 h-8 rounded-full"
                          />
                        )}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{review.author_name}</p>
                          <div className="flex text-yellow-400">
                            {renderStars(review.rating)}
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300">{review.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowShareModal(false)}
        >
          <div 
            className="bg-white dark:bg-dark-secondary rounded-lg p-6 max-w-sm w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Share</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex justify-center space-x-4">
              <FacebookShareButton url={shareUrl} title={shareTitle}>
                <FacebookIcon size={32} round />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={shareTitle}>
                <TwitterIcon size={32} round />
              </TwitterShareButton>
              <LinkedinShareButton url={shareUrl} title={shareTitle} summary={shareDescription}>
                <LinkedinIcon size={32} round />
              </LinkedinShareButton>
              <WhatsappShareButton url={shareUrl} title={shareTitle}>
                <WhatsappIcon size={32} round />
              </WhatsappShareButton>
              <EmailShareButton url={shareUrl} subject={shareTitle} body={shareDescription}>
                <EmailIcon size={32} round />
              </EmailShareButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}